import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api, ERROR_TEXT, EMPTY_TEXT, SUCCESS_TEXT } from "@/lib/api";
import { PageHead, Skeleton } from "@/components/hm/Basics";

const INTRO = [
  "Este es un espacio tranquilo para compartir tu avance. Aquí puedes contar cómo te sientes, qué has entendido y qué te ha ayudado. No tienes que demostrar nada. Solo estar.",
  "Si quieres, puedes leer lo que otras personas han escrito. A veces ver palabras de alguien que está en un momento parecido ayuda a ordenar lo tuyo.",
  "Cuando estés listo, deja tu mensaje. Puede ser una frase o una idea breve. Lo importante es que sea tuya.",
];

const PostForm = ({ onPosted }) => {
  const [text, setText] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/community/posts", { text, anonymous });
      onPosted(data); setText(""); toast.success(SUCCESS_TEXT);
    } catch { toast.error(ERROR_TEXT); } finally { setSending(false); }
  };
  return (
    <form onSubmit={submit} className="journal" style={{ margin: "0 0 2rem" }} data-testid="community-form">
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={400} placeholder="Hoy he entendido que..." data-testid="community-input" aria-label="Tu mensaje" />
      <div className="journal-f">
        <label className="inline-flex items-center gap-2" style={{ cursor: "pointer" }}>
          <Checkbox checked={anonymous} onCheckedChange={(v) => setAnonymous(v === true)} data-testid="community-anonymous-checkbox" />
          Publicar como anónimo · {text.length}/400
        </label>
        <button type="submit" className="btn btn-primary btn-sm" disabled={sending || !text.trim()} data-testid="community-submit-button">
          {sending ? "Enviando..." : "Dejar mi mensaje"}
        </button>
      </div>
    </form>
  );
};

const Post = ({ p, onUpdate }) => {
  const toggle = async () => {
    try { const { data } = await api.post(`/community/posts/${p.id}/identify`); onUpdate(data); }
    catch { toast.error(ERROR_TEXT); }
  };
  return (
    <div className={`msg ${p.mine ? "me" : ""}`} style={p.mine ? { justifySelf: "end" } : undefined} data-testid={`community-post-${p.id}`}>
      {p.text}
      <div className="msg-actions">
        <small style={{ margin: 0 }}>
          {p.mine ? (p.anonymous ? "Tú, como anónimo" : "Tú") : p.name} · {new Date(p.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long" })}
        </small>
        {!p.mine ? (
          <button className={`identify-btn ${p.identified_by_me ? "on" : ""}`} onClick={toggle} data-testid={`post-identify-${p.id}`}>
            <Heart size={13} fill={p.identified_by_me ? "currentColor" : "none"} /> Me identifico{p.identified_count > 0 && ` · ${p.identified_count}`}
          </button>
        ) : p.identified_count > 0 && <small style={{ margin: 0 }}>{p.identified_count} se identifican</small>}
      </div>
    </div>
  );
};

export default function Comunidad() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get("/community/posts").then((r) => setPosts(r.data)).catch(() => setError(true)); }, []);
  const update = (np) => setPosts((prev) => prev.map((x) => (x.id === np.id ? np : x)));

  return (
    <div style={{ maxWidth: 760 }} data-testid="comunidad-page">
      <PageHead eyebrow="Comunidad" title="Aquí" serif="no estás solo." />
      <div className="text-muted" style={{ display: "grid", gap: ".8rem", margin: "-1rem 0 2rem" }} data-testid="community-intro">
        {INTRO.map((p) => <p key={p} style={{ margin: 0 }}>{p}</p>)}
      </div>
      <PostForm onPosted={(p) => setPosts((prev) => [p, ...(prev || [])])} />
      {error && <p className="text-danger" data-testid="community-error">{ERROR_TEXT}</p>}
      {!posts && !error && <Skeleton n={3} h={70} />}
      {posts?.length === 0 && <p className="text-muted" data-testid="community-empty">{EMPTY_TEXT}</p>}
      <div className="msg-feed" data-testid="community-posts">
        {posts?.map((p) => <Post key={p.id} p={p} onUpdate={update} />)}
      </div>
    </div>
  );
}
